import React, { useEffect, useRef, useState } from 'react';
import { CheckIcon, ChevronDownIcon, PencilIcon, UserIcon, XIcon } from 'lucide-react';
import type { DetailField } from '../data/loanDetails';
type DetailFieldSectionProps = {
  title: string;
  left: DetailField[];
  right: DetailField[];
  onChange: (label: string, value: string) => void;
};
const indicatorTone = {
  green: 'bg-success',
  amber: 'bg-amber-400',
  red: 'bg-danger'
} as const;
const indicatorOptions = ['green', 'amber', 'red'] as const;
function FieldRow({
  field,
  onChange
}: {
  field: DetailField;
  onChange: (value: string) => void;
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(field.value ?? field.indicator ?? '');
  const inputRef = useRef<HTMLInputElement>(null);
  const fieldId = `field-${field.label.replace(/\W+/g, '-').toLowerCase()}`;
  useEffect(() => {
    if (editing) inputRef.current?.focus();
  }, [editing]);
  const start = () => {
    setDraft(field.value ?? field.indicator ?? '');
    setEditing(true);
  };
  const commit = () => {
    onChange(draft.trim());
    setEditing(false);
  };
  const cancel = () => {
    setDraft(field.value ?? field.indicator ?? '');
    setEditing(false);
  };
  const onKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      commit();
    }
    if (event.key === 'Escape') {
      event.preventDefault();
      cancel();
    }
  };
  return <div className="group flex items-start gap-2 border-b border-gray-100 py-2.5" data-id="element-139" data-mp="mbsb3qocqdxa">
      <div className="min-w-0 flex-1" data-id="element-140" data-mp="r9lg4gdx51vz">
        <label htmlFor={fieldId} className="text-[12px] font-medium text-ink" data-id="element-141" data-mp="w8540mc2onqz">
          {field.label}
        </label>

        <div className="mt-1 min-h-[24px]" data-id="element-142" data-mp="10zdfrp0vkl7b">
          {editing ? <div className="flex items-center gap-1.5" data-id="element-143" data-mp="110ojjoublsyf">
              {field.indicator ? <select id={fieldId} value={draft} onChange={event => setDraft(event.target.value)} onKeyDown={onKeyDown} className="w-full rounded-md border border-brand bg-white px-2 py-1 text-[12.5px] text-ink focus:outline-none focus:ring-2 focus:ring-brand/20" data-id="element-144" data-mp="3hn3nt2qh0h9">
                  {indicatorOptions.map(option => <option key={option} value={option} data-id="element-145" data-mp="5832lpf1bkyk">
                      {option}
                    </option>)}
                </select> : <input id={fieldId} ref={inputRef} value={draft} onChange={event => setDraft(event.target.value)} onKeyDown={onKeyDown} className="w-full rounded-md border border-brand bg-white px-2 py-1 text-[12.5px] text-ink focus:outline-none focus:ring-2 focus:ring-brand/20" data-id="element-146" data-mp="39mt4tm6yv8gi" />}
              <button type="button" onClick={commit} aria-label={`Save ${field.label}`} className="rounded p-1 text-success transition-colors hover:bg-success-soft" data-id="element-147" data-mp="xy1v5usfu07z">
                <CheckIcon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-148" data-mp="2kv9s8ocznmqf" />
              </button>
              <button type="button" onClick={cancel} aria-label={`Cancel editing ${field.label}`} className="rounded p-1 text-gray-400 transition-colors hover:bg-gray-100" data-id="element-149" data-mp="26w1pjlhm9whh">
                <XIcon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-150" data-mp="33kzjj1hjlqdl" />
              </button>
            </div> : field.indicator ? <span className={['inline-block h-4 w-4 rounded-full', indicatorTone[field.indicator]].join(' ')} role="img" aria-label={`Status indicator: ${field.indicator}`} data-id="element-151" data-mp="2psjlyg7eukk2" /> : field.person && field.value ? <span className="inline-flex items-center gap-1.5" data-id="element-152" data-mp="3naa0sjwxsjdt">
              <span className="flex h-4 w-4 items-center justify-center rounded-full bg-navy text-white" data-id="element-153" data-mp="1ynfi41g53md3">
                <UserIcon className="h-2.5 w-2.5" aria-hidden="true" data-id="element-154" data-mp="1tlzvi4ybd7sl" />
              </span>
              <a href={`#/people/${field.value}`} onClick={event => event.preventDefault()} className="text-[12.5px] text-brand underline decoration-brand/40 underline-offset-2 hover:text-brand-dark hover:decoration-brand" data-id="element-155" data-mp="1pmr3b4ix9hz5">
                {field.value}
              </a>
            </span> : <button type="button" onClick={start} className="w-full rounded text-left text-[12.5px] text-gray-700 hover:text-ink" data-id="element-156" data-mp="28k0ksg4osldr">
              {field.value || <span className="text-gray-300" data-id="element-157" data-mp="311whp5sf14yl">Add {field.label.toLowerCase()}</span>}
            </button>}
        </div>
      </div>

      {!editing && <button type="button" onClick={start} aria-label={`Edit ${field.label}`} className="mt-5 shrink-0 rounded p-1 text-gray-300 opacity-0 transition-opacity hover:bg-gray-100 hover:text-ink focus:opacity-100 group-hover:opacity-100" data-id="element-158" data-mp="33mcct4dt98mf">
          <PencilIcon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-159" data-mp="3hltt0vd1jdk6" />
        </button>}
    </div>;
}
export function DetailFieldSection({
  title,
  left,
  right,
  onChange
}: DetailFieldSectionProps) {
  const [open, setOpen] = useState(true);
  return <section className="overflow-hidden rounded-xl border border-gray-200 bg-white" data-id="element-160" data-mp="3rhf3lhw6dm5j">
      <button type="button" onClick={() => setOpen(prev => !prev)} aria-expanded={open} className="flex w-full items-center gap-2 border-b border-gray-200 bg-gray-50 px-5 py-3.5 text-left transition-colors hover:bg-gray-100" data-id="element-161" data-mp="5lz03bzlra6">
        <ChevronDownIcon className={['h-4 w-4 text-brand transition-transform', open ? 'rotate-0' : '-rotate-90'].join(' ')} aria-hidden="true" data-id="element-162" data-mp="e5mzvis94ypn" />
        <span className="font-display text-[14px] font-semibold text-navy" data-id="element-163" data-mp="267fw8ddpy1k3">{title}</span>
        <span className="ml-auto text-[11.5px] text-gray-400" data-id="element-164" data-mp="2jyl0jss17i0r">Click a value to edit</span>
      </button>

      {open && <div className="grid grid-cols-1 gap-x-10 px-5 py-1.5 md:grid-cols-2" data-id="element-165" data-mp="2f5i67iffk4ji">
          <div data-id="element-166" data-mp="2tueb56m6x1fw">
            {left.map(field => <FieldRow key={field.label} field={field} onChange={value => onChange(field.label, value)} data-id="element-167" data-mp="2owmksh171ndt" />)}
          </div>
          <div data-id="element-168" data-mp="2xxawv6711do2">
            {right.map(field => <FieldRow key={field.label} field={field} onChange={value => onChange(field.label, value)} data-id="element-169" data-mp="2xxu51v6vknow" />)}
          </div>
        </div>}
    </section>;
}